import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useCtmAdCRUDActions } from '@mm/state/actions/CtmAdCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useTriggerCRUDActions } from '@mm/state/actions/TriggerCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { DefaultResponseType } from '@mm/state/model/ChatbotModel';
import TriggerModel, {
  getTriggerChannelByKind,
  getTriggerTitleByKind,
  isTrigger,
} from '@mm/state/model/TriggerModel';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { welcomePageSelector } from '@mm/state/selectors/onboardingSelectors';
import { selectPages } from '@mm/state/selectors/pageSelectors';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from '../../../../../../../common/hooks/use-previous';
import styles from './InputTriggers.module.css';
import { useChannelConnection } from './use-channel-connection';
import { useInputTriggerCRUD } from './use-input-trigger-crud';
import { isPromoter } from '@mm/state/model/PromoterModel';
import {
  mapPromoterTypeToTriggerType,
  TRIGGER_DEFINITIONS,
} from './TriggersDefinitions';
import { getPromoterOrTrigger } from '@mm/state/actions/automationActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import {
  CampaignAudienceAttribute,
  CampaignType,
} from '@mm/state/model/CampaignsModel';
import { ChannelType } from '../../../../../liveChat/components/ChannelIcon';
import {
  getAdsSubtitle,
  getAudiencesSubtitle,
  getSubtitleByPromoter,
  getWebchatSubtitle,
} from './get-subtitle-helper';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectInstagramMedia } from '@mm/state/selectors/instagramSelectors';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export interface InputTrigger {
  active: boolean;
  label: string;
  error?: string;
  subtitle?: string;
  subtitleImage?: string;
  subtitleStyleLight?: boolean;
  audienceId?: number;
  pageId?: number;
  className?: string;
  keywords?: string[] | null;
  onClick: () => void;
  type: string;
  channel?: ChannelType;
  setActive: null | ((active: boolean) => void);
  delete: null | (() => void);
  remove?: null | (() => void);
  viewsCount?: number | null;
  onMount?: () => void;
}

export type TAutomationInputTriggerType =
  CampaignAudienceAttribute['triggerType'];

export const getInputTriggerName = (source?: any): string => {
  if (isPromoter(source) && source.kind) {
    const kind = mapPromoterTypeToTriggerType(source.kind);
    return TRIGGER_DEFINITIONS[kind]?.name || '';
  }

  if (isTrigger(source) && source.kind) {
    return TRIGGER_DEFINITIONS[source.kind]?.name || '';
  }

  return source?.name || '';
};

export const getInputTriggerPageId = (source?: any): number => {
  if (source.kind === 'getemails') {
    return 0;
  }

  if (isTrigger(source)) {
    if (source.kind === 'welcome_page') {
      return source.id;
    }

    return source.botResponses[0]?.pageId;
  }

  if (isPromoter(source)) {
    return source.pageId;
  }

  return -1;
};

export const getCampaignInputTriggerAudienceId = (
  campaign: CampaignType,
  triggerType: TAutomationInputTriggerType,
  id: number,
) => {
  return campaign?.campaignAudiencesAttributes?.find(
    attr =>
      triggerType &&
      attr.triggerType === triggerType &&
      attr[triggerType]?.id.toString() === id.toString(),
  )?.audienceId;
};

export const useInputTriggersIdentification = (campaignId?: number) => {
  const triggerCRUDActions = useTriggerCRUDActions();
  const promoterCRUDActions = usePromoterCRUDActions();
  const chatbotCRUDActions = useChatbotCRUDActions();
  const { getIGMedia, clearIGMedia } = useInstagramCRUDActions();
  const viewActions = useViewActions();
  const modalActions = useModalActions();
  const inputTriggerCRUD = useInputTriggerCRUD();
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const {
    connectChannel,
    isInstagramConnected,
    isSMSConnected,
    isFacebookConnected,
    isEmailConnected,
  } = useChannelConnection(() => {});
  const campaign = useSelector((state: StoreState) =>
    state.campaigns.entities.find(c => c.id === campaignId),
  );
  const facebookPage = useSelector(activeFacebookPageSelector);

  const welcomePage = useSelector(welcomePageSelector);
  const triggers = useSelector((state: StoreState) => state.trigger.entities);
  const promoters = useSelector((state: StoreState) => state.promoter.entities);
  const instagramMedia = useSelector(selectInstagramMedia);

  const bot = useSelector(activeChatbotSelector);
  const botId = bot?.id;
  const catchAllDisabled =
    bot?.noMatchResponse !== DefaultResponseType.CatchAllEnagagements;
  const audiences = useSelector(selectAudienceSorted);
  const ctmAdCRUDActions = useCtmAdCRUDActions();
  const pageCRUDActions = usePageCRUDActions();
  const { openDeleteConfirmModal } = useModalActions();

  const pages = useSelector(selectPages);

  const getPage = (id: number) => {
    return pages.find(page => page.id.toString() === id?.toString());
  };

  const { pixelName: whitelabelPixelName } = useWhitelabelConfig();
  const pixelName = whitelabelPixelName
    ? `${whitelabelPixelName} Lead Generator`
    : 'X-Ray Lead Generation Pixel';

  const [loading, setLoading] = useState(true);

  const widgetCRUDActions = useWidgetCRUDActions();

  const invalidate = useCallback(async () => {
    await Promise.all([
      audiencesCRUDActions.retrieveAll(botId),
      audiencesCRUDActions.getAudienceVariable(botId),
    ]);
    setLoading(false);
  }, [botId, setLoading]);

  const prevCount = usePrevious(0);

  useEffect(() => {
    if (
      campaign &&
      campaign.campaignAudiencesAttributes &&
      campaign.campaignAudiencesAttributes.length >= prevCount
    ) {
      invalidate();
    }
  }, [campaign?.campaignAudiencesAttributes?.length, campaign?.id]);

  const getList = () => {
    return (
      campaign?.campaignAudiencesAttributes
        ?.filter(attr => !attr.groupName)
        // tslint:disable-next-line: cyclomatic-complexity
        .map(attr => {
          const source = getPromoterOrTrigger(attr);
          // welcome page
          if (source?.kind === 'welcome_page' && welcomePage) {
            return {
              type: 'facebook',
              kind: source?.kind,
              channel: ChannelType.facebook,
              active: !bot.fbWelcomerDisabled,
              audienceId: attr.audienceId,
              label: 'Facebook DM Autoresponder',
              subtitle: getSubtitleByPromoter(source),
              subtitleStyleLight: true,
              pageId: welcomePage.id,
              keywords: null,
              viewsCount: welcomePage.viewsCount,
              className: 'trigger-card__fb-dm-autoresponder',
              error: '',
              onClick: () =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : viewActions.showSlideoutMenu('fbWelcomerEditor'),
              setActive: active =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : chatbotCRUDActions.update({
                      ...bot,
                      fbWelcomerDisabled: !active,
                    }),
              delete: () => {
                if (campaign) {
                  return inputTriggerCRUD.removeTriggerFromAutomation({
                    campaign,
                    audienceId: attr.audienceId,
                  });
                }
              },
            };
          }

          const triggerMatch = triggers.find(t => t.id === source?.id);

          // fb ig webchat keywords
          if (
            [
              'instagram_keywords',
              'facebook_keywords',
              'webchat_keywords',
            ].includes(source?.kind || '') &&
            triggerMatch
          ) {
            const keywords = triggerMatch.phrases.map(p => p.phrase);
            const pageId = triggerMatch.botResponses[0]?.pageId;
            const page = getPage(pageId);
            const channel = getTriggerChannelByKind(
              triggerMatch.kind,
            ) as ChannelType;
            const isInstagram = channel === 'instagram';
            const isFacebook = channel === 'facebook';

            return {
              type: 'general',
              kind: source?.kind,
              channel,
              active: triggerMatch.active,
              audienceId: attr.audienceId,
              pageId,
              label: getTriggerTitleByKind(triggerMatch.kind),
              subtitle: getSubtitleByPromoter(triggerMatch),
              subtitleStyleLight: true,
              keywords: keywords.filter((k, index) => index < 2),
              error:
                keywords.length === 0
                  ? 'Add a keyword to make the trigger work'
                  : '',
              viewsCount: page?.viewsCount,
              onClick: () => {
                if (isInstagram && !isInstagramConnected) {
                  connectChannel('instagram');
                } else if (isFacebook && !isFacebookConnected) {
                  connectChannel('facebook');
                } else {
                  viewActions.showSlideoutMenu('qaEditor', {
                    triggerId: triggerMatch.id,
                    campaignId,
                    audienceId: attr.audienceId,
                  });
                }
              },

              delete: () => {
                triggerCRUDActions.delete(triggerMatch);
                return inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });
              },
              setActive: active => {
                if (isInstagram && !isInstagramConnected) {
                  connectChannel('instagram');
                } else if (isFacebook && !isFacebookConnected) {
                  connectChannel('facebook');
                } else {
                  triggerCRUDActions.update({ ...triggerMatch, active });
                }
              },
            };
          }

          // sms keywords
          if (['sms_keywords'].includes(source?.kind || '') && triggerMatch) {
            const keywords = triggerMatch.phrases.map(p => p.phrase);
            const pageId = triggerMatch.botResponses[0]?.pageId;
            const page = getPage(pageId);

            return {
              type: 'sms',
              kind: source?.kind,
              channel: ChannelType.sms,
              active: triggerMatch.active,
              pageId,
              audienceId: attr.audienceId,
              label: `SMS Keyword`,
              subtitle: getSubtitleByPromoter(triggerMatch),
              subtitleStyleLight: true,
              keywords: keywords.filter((k, index) => index < 2),
              error: !keywords?.length
                ? 'Add a keyword to make the trigger work'
                : '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                isSMSConnected
                  ? viewActions.showSlideoutMenu('smsKeywordEditor', {
                      triggerId: triggerMatch.id,
                      campaignId,
                      audienceId: attr.audienceId,
                    })
                  : {},
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });
                return triggerCRUDActions.delete({
                  id: triggerMatch.id,
                } as TriggerModel);
              },

              setActive: null,
            };
          }

          const igWelcomer = promoters.find(w => w.id === source?.id);
          if (
            ['instagram_welcomer'].includes(source?.kind || '') &&
            igWelcomer
          ) {
            const page = getPage(igWelcomer.pageId);
            return {
              type: 'instagram',
              kind: source?.kind,
              channel: ChannelType.instagram,
              active: igWelcomer.active,
              audienceId: attr.audienceId,
              pageId: igWelcomer.pageId,
              label: 'Instagram DM Autoresponder',
              subtitle: getSubtitleByPromoter(igWelcomer),
              subtitleStyleLight: true,
              keywords: null,
              error: '',
              viewsCount: page?.viewsCount,
              className: 'trigger-card__ig-dm-autoresponder',
              onClick: () =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : viewActions.showSlideoutMenu('igWelcomerEditor'),
              delete: () => {
                return inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });
              },
              setActive: active =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : promoterCRUDActions.update(botId, {
                      ...igWelcomer,
                      active,
                    }),
            };
          }

          // if comment guard
          const igCommentGuard = promoters.find(cg => cg.id === source?.id);
          const currentPost = instagramMedia.find(
            media => igCommentGuard?.postId === media.id,
          );
          const isVideoPost = currentPost?.mediaType === 'VIDEO';
          const keywords = !igCommentGuard?.keywordsEnabled
            ? []
            : igCommentGuard.keywords;
          if (
            igCommentGuard &&
            ['instagram_comment_guard'].includes(source?.kind || '')
          ) {
            const page = getPage(igCommentGuard.pageId);
            return {
              type: 'instagram',
              kind: source?.kind,
              channel: ChannelType.instagram,
              active: igCommentGuard.active,
              audienceId: attr.audienceId,
              pageId: igCommentGuard.pageId,
              label: 'Instagram Comment Autoresponder',
              subtitle: getSubtitleByPromoter(igCommentGuard),
              subtitleStyleLight: !!igCommentGuard.keywordsEnabled,
              subtitleImage:
                currentPost && !isVideoPost
                  ? currentPost?.mediaUrl
                  : currentPost && isVideoPost
                  ? currentPost?.thumbnailUrl
                  : undefined,
              keywords: keywords?.filter((k, index) => index < 2),
              error: '',
              viewsCount: page?.viewsCount,
              onMount: () =>
                ['id', 'ALL'].includes(igCommentGuard?.postId || '')
                  ? clearIGMedia()
                  : facebookPage?.id &&
                    igCommentGuard.postId &&
                    getIGMedia(facebookPage.id, igCommentGuard.postId),
              onClick: () =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : viewActions.showSlideoutMenu('igCommentGuardEditor', {
                      promoterId: igCommentGuard.id,
                    }),
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });
                promoterCRUDActions.delete(igCommentGuard);
              },
              setActive: active =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : promoterCRUDActions.update(botId, {
                      ...igCommentGuard,
                      active,
                    }),
            };
          }

          const fbCommentGuard = promoters.find(cg => cg.id === source?.id);
          if (
            fbCommentGuard &&
            ['fb_comment_guard'].includes(source?.kind || '')
          ) {
            const page = getPage(fbCommentGuard.pageId);
            const keywords = !fbCommentGuard.keywordsEnabled
              ? []
              : fbCommentGuard.keywords;
            return {
              type: 'facebook',
              kind: source?.kind,
              channel: ChannelType.facebook,
              active: fbCommentGuard.active,
              audienceId: attr.audienceId,
              pageId: fbCommentGuard.pageId,
              label: 'Facebook Comment Autoresponder',
              subtitle: getSubtitleByPromoter(fbCommentGuard),
              subtitleImage:
                fbCommentGuard.postId && fbCommentGuard.postId !== 'ALL'
                  ? fbCommentGuard?.postInfo?.picture
                  : undefined,
              subtitleStyleLight: !!fbCommentGuard.keywordsEnabled,
              keywords: keywords?.filter((k, index) => index < 2),
              error: '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : viewActions.showSlideoutMenu('fbCommentGuardEditor', {
                      promoterId: fbCommentGuard.id,
                    }),
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });

                promoterCRUDActions.delete(fbCommentGuard);
              },
              setActive: active =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : promoterCRUDActions.update(botId, {
                      ...fbCommentGuard,
                      active,
                    }),
            };
          }
          const igStoryMention = promoters.find(cg => cg.id === source?.id);
          if (
            igStoryMention &&
            ['instagram_story_mention'].includes(source?.kind || '')
          ) {
            const page = getPage(igStoryMention.pageId);
            return {
              type: 'instagram',
              kind: source?.kind,
              channel: ChannelType.instagram,
              active: igStoryMention.active,
              audienceId: attr.audienceId,
              pageId: igStoryMention.pageId,
              label: 'Instagram Story Mention',
              subtitle: getSubtitleByPromoter(igStoryMention),
              subtitleStyleLight: true,
              keywords: null,
              error: '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : viewActions.showSlideoutMenu('igStoryMentionEditor', {
                      promoterId: igStoryMention.id,
                    }),
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });

                promoterCRUDActions.delete(igStoryMention);
              },
              setActive: active =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : promoterCRUDActions.update(botId, {
                      ...igStoryMention,
                      active,
                    }),
            };
          }
          const igStoryReaction = promoters.find(cg => cg.id === source?.id);
          if (
            igStoryReaction &&
            ['instagram_story_reaction'].includes(source?.kind || '')
          ) {
            const page = getPage(igStoryReaction.pageId);
            return {
              type: 'instagram',
              kind: source?.kind,
              channel: ChannelType.instagram,
              active: igStoryReaction.active,
              audienceId: attr.audienceId,
              pageId: igStoryReaction.pageId,
              label: 'Instagram Story Reaction',
              subtitle: getSubtitleByPromoter(igStoryReaction),
              subtitleStyleLight: !!igStoryReaction.keywordsEnabled,
              keywords: keywords?.filter((k, index) => index < 2),
              error: '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : viewActions.showSlideoutMenu('igStoryReactionEditor', {
                      promoterId: igStoryReaction.id,
                    }),
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });

                promoterCRUDActions.delete(igStoryReaction);
              },
              setActive: active =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : promoterCRUDActions.update(botId, {
                      ...igStoryReaction,
                      active,
                    }),
            };
          }

          const igLive = promoters.find(p => p.id === source?.id);
          if (
            igLive &&
            ['instagram_live_comment_guard'].includes(source?.kind || '')
          ) {
            const page = getPage(igLive.pageId);
            return {
              type: 'instagram',
              kind: source?.kind,
              channel: ChannelType.instagram,
              active: igLive.active,
              audienceId: attr.audienceId,
              pageId: igLive.pageId,
              label: 'Instagram Live',
              subtitle: getSubtitleByPromoter(igLive),
              subtitleStyleLight: !!igLive.keywordsEnabled,
              keywords: keywords?.filter((k, index) => index < 2),
              error: '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : viewActions.showSlideoutMenu('igLiveEditor', {
                      promoterId: igLive.id,
                    }),
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });

                promoterCRUDActions.delete(igLive);
              },
              setActive: active =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : promoterCRUDActions.update(botId, {
                      ...igLive,
                      active,
                    }),
            };
          }
          const fbShareLink = promoters.find(link => link.id === source?.id);
          if (fbShareLink && ['link'].includes(source?.kind || '')) {
            const page = getPage(fbShareLink.pageId);
            return {
              type: 'facebook',
              kind: source?.kind,
              channel: ChannelType.facebook,
              active: fbShareLink.active,
              audienceId: attr.audienceId,
              pageId: fbShareLink.pageId,
              label: 'Facebook Share Link',
              subtitle: getSubtitleByPromoter(fbShareLink),
              keywords: null,
              error: '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : viewActions.showSlideoutMenu('shareLinksEditor', {
                      promoterId: fbShareLink.id,
                      platform: 'facebook',
                    }),
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });

                promoterCRUDActions.delete(fbShareLink);
              },
              setActive: active =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : promoterCRUDActions.update(botId, {
                      ...fbShareLink,
                      active,
                    }),
            };
          }

          const igShareLink = promoters.find(link => link.id === source?.id);
          if (igShareLink && ['instagram_link'].includes(source?.kind || '')) {
            const page = getPage(igShareLink.pageId);
            return {
              type: 'instagram',
              kind: source?.kind,
              channel: ChannelType.instagram,
              active: igShareLink.active,
              audienceId: attr.audienceId,
              pageId: igShareLink.pageId,
              label: 'Instagram Share Link',
              subtitle: getSubtitleByPromoter(igShareLink),
              keywords: null,
              error: '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : viewActions.showSlideoutMenu('shareLinksEditor', {
                      promoterId: igShareLink.id,
                      platform: 'instagram',
                    }),
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });

                promoterCRUDActions.delete(igShareLink);
              },
              setActive: active =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : promoterCRUDActions.update(botId, {
                      ...igShareLink,
                      active,
                    }),
            };
          }
          const igCatchAll = promoters.find(w => w.id === source?.id);
          if (
            igCatchAll &&
            ['instagram_catch_all'].includes(source?.kind || '')
          ) {
            const page = getPage(igCatchAll.pageId);
            return {
              type: 'instagram',
              kind: source?.kind,
              channel: ChannelType.instagram,
              active: igCatchAll.active,
              audienceId: attr.audienceId,
              pageId: igCatchAll.pageId,
              label: 'Instagram Engagement Autoresponder',
              subtitle: getSubtitleByPromoter(igCatchAll),
              subtitleStyleLight: true,
              className: 'trigger-card__engagements-autoresponder',
              keywords: null,
              error: catchAllDisabled
                ? 'This Autoresponder is disabled in Settings'
                : '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : viewActions.showSlideoutMenu('engagementEditor', {
                      promoterId: igCatchAll.id,
                      platform: 'instagram',
                    }),
              delete: null,
              setActive: active =>
                !isInstagramConnected
                  ? connectChannel('instagram')
                  : promoterCRUDActions.update(botId, {
                      ...igCatchAll,
                      active,
                    }),
            };
          }

          const fbCatchAll = promoters.find(w => w.id === source?.id);
          if (
            fbCatchAll &&
            ['facebook_catch_all'].includes(source?.kind || '')
          ) {
            const page = getPage(fbCatchAll.pageId);
            return {
              type: 'facebook',
              kind: source?.kind,
              channel: ChannelType.facebook,
              active: fbCatchAll.active,
              audienceId: attr.audienceId,
              pageId: fbCatchAll.pageId,
              label: 'Facebook Engagement Autoresponder',
              subtitleStyleLight: true,
              subtitle: getSubtitleByPromoter(fbCatchAll),
              className: 'trigger-card__engagements-autoresponder',
              keywords: null,
              error: catchAllDisabled
                ? 'This Autoresponder is disabled in Settings'
                : '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : viewActions.showSlideoutMenu('engagementEditor', {
                      promoterId: fbCatchAll.id,
                      platform: 'facebook',
                    }),
              delete: null,
              setActive: active =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : promoterCRUDActions.update(botId, {
                      ...fbCatchAll,
                      active,
                    }),
            };
          }

          const getemailsPromoter = promoters.find(p => p.id === source?.id);
          if (getemailsPromoter && ['getemails'].includes(source?.kind || '')) {
            return {
              type: 'getemails',
              kind: source?.kind,
              channel: ChannelType.email,
              active: getemailsPromoter.active,
              audienceId: attr.audienceId,
              pageId: getemailsPromoter.pageId,
              label: pixelName,
              subtitle: getWebchatSubtitle(getemailsPromoter),
              keywords: null,
              error: '',
              viewsCount: getemailsPromoter.sessionsStartedCount || 0,
              onClick: () =>
                !isEmailConnected
                  ? modalActions.openActivateEmailAccountModal()
                  : viewActions.showSlideoutMenu('getemailsEditorNew', {
                      promoterId: getemailsPromoter.id,
                    }),
              delete: () => {
                modalActions.openGeneralConfirmModal({
                  headerText: 'Delete X-Ray Completely',
                  messageText:
                    'Are you sure you want to delete this X-Ray? This action cannot be undone',
                  onCloseHandler: ok => {
                    if (ok) {
                      inputTriggerCRUD.removeTriggerFromAutomation({
                        campaign,
                        audienceId: attr.audienceId,
                      });

                      promoterCRUDActions.delete(getemailsPromoter);
                    }
                  },
                  okButtonText: "Yes I'm sure",

                  cancelButtonText: 'Cancel',
                });
              },
              remove: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });
              },
              setActive: active =>
                promoterCRUDActions.update(botId, {
                  ...getemailsPromoter,
                  active,
                }),
            };
          }

          const customerChat = promoters.find(p => p.id === source?.id);
          if (customerChat && ['customer_chat'].includes(source?.kind || '')) {
            const page = getPage(customerChat.pageId);

            return {
              type: 'webchatWidget',
              kind: source?.kind,
              channel: ChannelType.webchat,
              active: customerChat.active,
              audienceId: attr.audienceId,
              pageId: customerChat.pageId,
              label: 'Website Chat Widget',
              subtitle: getWebchatSubtitle(customerChat),
              keywords: null,
              error: '',
              viewsCount: page?.viewsCount,
              onClick: () =>
                viewActions.showSlideoutMenu('customerChatSlideoutEditor', {
                  promoterId: customerChat.id,
                }),
              delete: () => {
                inputTriggerCRUD.removeTriggerFromAutomation({
                  campaign,
                  audienceId: attr.audienceId,
                });

                promoterCRUDActions.delete(customerChat);
              },
              setActive: active =>
                promoterCRUDActions.update(botId, {
                  ...customerChat,
                  active,
                }),
            };
          }

          const contactList = audiences.find(p => p.id === attr.audienceId);
          if (
            ['contacts_csv'].includes(source?.kind || '') ||
            attr.name?.toLowerCase().includes('csv')
          ) {
            const subtitle =
              attr.name?.match(/file: (.*?)( \(\d+\))?$/)?.[1] || '';
            return {
              type: 'contacts_csv',
              kind: source?.kind,
              channel: ChannelType.csv,
              active: true,
              audienceId: attr.audienceId,
              label: 'Upload CSV',
              keywords: null,
              subtitle,
              onMount: () =>
                audiencesCRUDActions.retrieveAudienceById(
                  botId,
                  attr.audienceId,
                ),
              onClick: () =>
                viewActions.showSlideoutMenu('editAudience', {
                  audienceId: attr.audienceId,
                  className: styles.audienceSlideout,
                }),
              delete: () => {
                inputTriggerCRUD.removeContactListTrigger({
                  campaign,
                  audienceId: attr.audienceId,
                });
              },
              setActive: null,
              viewsCount: contactList?.contactsCount,
            };
          }

          if (attr.ctmAd) {
            const page = getPage(attr.ctmAd.ad.pageId);
            return {
              type: 'facebook',
              kind: 'ctm_ads',
              channel: ChannelType.facebook,
              active: !attr.ctmAd.ad.adAccountId || !attr.ctmAd.ad.draft, // !attr.ctmAd.ad.adAccountId means connected JSON manually
              audienceId: attr.audienceId,
              pageId: attr.ctmAd.ad.pageId,
              label: 'Click-to-Messenger Ad',
              subtitle: getAdsSubtitle(attr.ctmAd),
              error: '',
              viewsCount: page?.viewsCount,
              onMount: async () => {
                if (page?.pageGroupId) {
                  await pageCRUDActions.retrievePagesByGroupId({
                    pageGroupId: page.pageGroupId,
                    showWidgets: true,
                  });
                }
              },
              onClick: () =>
                !isFacebookConnected
                  ? connectChannel('facebook')
                  : viewActions.showSlideoutMenu('ctmAdsEditor', {
                      ad: attr.ctmAd,
                      campaignId: campaign.id,
                    }),
              delete: () => {
                const deleteCallback = () => {
                  if (attr.ctmAd) {
                    ctmAdCRUDActions.delete(botId, attr.ctmAd);
                  }
                  inputTriggerCRUD.removeTriggerFromAutomation({
                    campaign,
                    audienceId: attr.audienceId,
                  });
                };
                if (!attr?.ctmAd?.ad.adAccountId || !attr?.ctmAd?.ad.draft) {
                  openDeleteConfirmModal({
                    newStyle: true,
                    headerText: `Deleting this ad won't delete it from Facebook Ads Manager. 
                  Are you sure you want to delete this ad?`,
                    deleteCallback,
                  });
                } else {
                  deleteCallback();
                }
              },
              setActive: null,
            };
          }

          return {
            type: 'unknown',
            kind: 'unknown',
            channel: undefined,
            active: false,
            audienceId: attr.audienceId,
            label: 'Unknown',
            subtitle: '',
            keywords: null,
            onMount: () => {},
            onClick: () => {},
            delete: () => {
              inputTriggerCRUD.removeTriggerFromAutomation({
                campaign,
                audienceId: attr.audienceId,
              });
            },
            setActive: null,
            viewsCount: 0,
          };
        }) || []
    );
  };

  const getAudienceTrigger = () => {
    const audiencesWithGroupName =
      campaign?.campaignAudiencesAttributes?.filter(attr => attr.groupName);

    if (audiencesWithGroupName?.length) {
      return {
        active: true,
        type: 'audience',
        channel: ChannelType.audience,
        label: 'Audience',
        pageId: undefined,
        keywords: [audiencesWithGroupName?.[0].name],
        subtitleStyleLight: true,
        subtitle: getAudiencesSubtitle(audiencesWithGroupName),
        onClick: () =>
          viewActions.showSlideoutMenu('automationSelectAudience', {
            campaign,
          }),
        delete: () => {
          if (campaign) {
            inputTriggerCRUD.removeAudiencesTrigger({
              campaign,
              audienceIds: audiencesWithGroupName.map(attr => attr.audienceId),
            });
          }
        },
        setActive: null,
        viewsCount: audiencesWithGroupName?.reduce(
          (sum, cur) => sum + Number(cur?.contactsCount),
          0,
        ),
      };
    }
    return {};
  };

  const mappedAudiencesIds = campaign?.campaignAudiencesAttributes
    ?.map(attr => attr.audienceId)
    .sort()
    .join('');

  const list = useMemo(
    () =>
      Object.keys(getAudienceTrigger()).length
        ? ([...getList(), getAudienceTrigger()] as InputTrigger[])
        : [...getList()],

    [
      campaign?.id,
      mappedAudiencesIds,
      welcomePage,
      audiences,
      pages,
      promoters,
      triggers,
      audiences,
    ],
  );

  useEffect(() => {
    if (list.length) {
      // grab widgets from all pages to support "empty dialogue" validation error message
      const pageIds = list.map(v => v?.pageId);
      const all = pageIds.map(
        pageId => pageId && widgetCRUDActions.retrieveAll(pageId),
      );

      (async () => {
        try {
          await Promise.all(all);
        } catch (err) {
          console.error(err);
        }
      })();
    }
  }, [list.length, campaign?.id]);

  if (!campaign || loading) {
    return null;
  }

  return list;
};
